import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../src/environment/environment';

interface CacheEntry {
  response: HttpResponse<any>;
  timestamp: number;
}

class ImageCacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

  get(url: string): HttpResponse<any> | null {
    const entry = this.cache.get(url);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - entry.timestamp > this.CACHE_DURATION;

    if (isExpired) {
      this.cache.delete(url);
      return null;
    }

    return entry.response;
  }

  set(url: string, response: HttpResponse<any>): void {
    this.cache.set(url, {
      response,
      timestamp: Date.now(),
    });
  }

  clear(): void {
    this.cache.clear();
  }

  // Clean up expired entries periodically
  cleanupExpired(): void {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    this.cache.forEach((entry, url) => {
      if (now - entry.timestamp > this.CACHE_DURATION) {
        entriesToDelete.push(url);
      }
    });

    entriesToDelete.forEach(url => this.cache.delete(url));
  }
}

// Singleton instance
const imageCacheService = new ImageCacheService();
const API_HOST = safeHost(environment.API_URL);

// Cleanup expired entries every 2 minutes
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      imageCacheService.cleanupExpired();
    },
    2 * 60 * 1000
  );
}

export const imageCacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Only cache GET requests for images from current API host
  if (req.method !== 'GET' || !isApiHost(req.url) || !isImageRequest(req.url)) {
    return next(req);
  }

  // Check if we have a cached response
  const cachedResponse = imageCacheService.get(req.url);
  if (cachedResponse) {
    return of(cachedResponse.clone());
  }

  // If not cached, make the request and cache the response
  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        imageCacheService.set(req.url, event.clone());
      }
    })
  );
};

function isImageRequest(url: string): boolean {
  // Check if URL contains image extensions or image-related paths
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.ico'];
  const lowerUrl = url.toLowerCase();

  return (
    imageExtensions.some(ext => lowerUrl.includes(ext)) ||
    lowerUrl.includes('/uploads/') ||
    lowerUrl.includes('/images/') ||
    lowerUrl.includes('/media/')
  );
}

function safeHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

function isApiHost(url: string): boolean {
  if (!API_HOST) {
    return false;
  }

  try {
    const host = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined).host;
    return host === API_HOST;
  } catch {
    return false;
  }
}

export { imageCacheService };
